import { formatPeriodLong } from '@commissionsight/shared';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../api.js';
import { PeriodPicker } from '../components/PeriodPicker.js';
import { WorkspaceFilter } from '../components/WorkspaceFilter.js';
import { Card, Field, Loading, inputClass } from '../components/ui.js';
import { formatCurrency } from '../lib/status.js';
import { useCarriers } from '../lib/useCarriers.js';

interface RdeBreakdownRow {
  key: string;
  disenrolled: number;
  rapid: number;
  clawedBackAmount: number;
}
interface RdeRadar {
  rdeMonths: number;
  asOf: string | null;
  realized: { count: number; clawedBackAmount: number };
  exposure: { count: number; atRiskAmount: number };
  disenrollments: { total: number; rapid: number; rapidRatePct: number };
  inferredDisenrollments: number;
  evaluated: number;
  total: number;
  byPlan: RdeBreakdownRow[];
  byAgent: RdeBreakdownRow[];
}
interface RdeResponse {
  period: string | null;
  carrierId: string;
  radar: RdeRadar | null;
}

interface RenewalForecast {
  horizonMonths: number;
  fromPeriod: string;
  annualRenewalValue: number;
  activeMembers: number;
  evaluated: number;
  total: number;
  byMonth: { period: string; amount: number; members: number }[];
  byPlan: { key: string; members: number; amount: number }[];
}
interface ForecastResponse {
  period: string | null;
  carrierId: string;
  ratesInferred: boolean;
  forecast: RenewalForecast | null;
}

/**
 * Medicare lifecycle — the rapid-disenrollment (RDE) clawback radar. CMS advances
 * Medicare commission and claws back the FULL amount if a member leaves inside the
 * first ~3 months; this surfaces realized clawbacks, the members still inside the
 * window (exposure you can see coming), and which plans / producers churn early.
 * Renewal/vesting forecast + AEP cohort views plug into this page next.
 */
export function MedicareRde() {
  const { baseUrl, token } = useAuth();
  const [searchParams] = useSearchParams();
  const { carriers } = useCarriers();
  const [carrierId, setCarrierId] = useState(searchParams.get('carrierId') ?? '');
  const [workspaceId, setWorkspaceId] = useState(searchParams.get('workspaceId') ?? '');
  const [period, setPeriod] = useState('');
  const [data, setData] = useState<RdeResponse | null>(null);
  const [forecast, setForecast] = useState<ForecastResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Default to the first carrier so the page is useful on open (the endpoint needs one).
  useEffect(() => {
    if (!carrierId && carriers[0]) setCarrierId(carriers[0].id);
  }, [carriers, carrierId]);

  useEffect(() => {
    if (!carrierId) return;
    let active = true;
    setLoading(true);
    setError(null);
    const qs = new URLSearchParams({ carrierId });
    if (period) qs.set('period', period);
    if (workspaceId) qs.set('workspaceId', workspaceId);
    fetch(`${baseUrl.replace(/\/$/, '')}/reports/medicare-rde?${qs}`, {
      headers: { authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`Failed (${r.status})`))))
      .then((b: RdeResponse) => {
        if (!active) return;
        setData(b);
        if (!period && b.period) setPeriod(b.period);
      })
      .catch((e) => active && setError(e instanceof Error ? e.message : 'Failed to load'))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [baseUrl, token, carrierId, period, workspaceId]);

  // Renewal & book-value forecast (independent of the RDE radar above).
  useEffect(() => {
    if (!carrierId) return;
    let active = true;
    setForecast(null);
    const qs = new URLSearchParams({ carrierId });
    if (period) qs.set('period', period);
    if (workspaceId) qs.set('workspaceId', workspaceId);
    fetch(`${baseUrl.replace(/\/$/, '')}/reports/medicare-renewal-forecast?${qs}`, {
      headers: { authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`Failed (${r.status})`))))
      .then((b: ForecastResponse) => active && setForecast(b))
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [baseUrl, token, carrierId, period, workspaceId]);

  const radar = data?.radar ?? null;
  const coverage =
    radar && radar.total > 0 ? Math.round((radar.evaluated / radar.total) * 100) : null;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Medicare lifecycle</h1>
          <p className="mt-1 max-w-2xl text-sm text-neutral-500">
            Both sides of the book. <strong>Downside:</strong> the rapid-disenrollment radar — CMS
            claws back the <em>full</em> advance when a member leaves inside the first{' '}
            {radar?.rdeMonths ?? 3} months. <strong>Upside:</strong> what the active book renews
            over the next year.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <WorkspaceFilter value={workspaceId} onChange={setWorkspaceId} />
          <div className="w-48">
            <Field label="Carrier">
              <select
                className={inputClass}
                value={carrierId}
                onChange={(e) => setCarrierId(e.target.value)}
              >
                <option value="">Select a carrier…</option>
                {carriers.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <div className="w-48">
            <Field label="Period">
              <PeriodPicker value={period} onChange={setPeriod} placeholder="Latest period" />
            </Field>
          </div>
        </div>
      </div>

      {error && <p className="rounded bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</p>}

      {!carrierId ? (
        <Card>
          <p className="py-8 text-center text-sm text-neutral-500">
            Pick a Medicare carrier to see its rapid-disenrollment exposure.
          </p>
        </Card>
      ) : loading && !radar ? (
        <Loading label="Reading the book…" />
      ) : !radar || radar.evaluated === 0 ? (
        <Card>
          <p className="py-8 text-center text-sm text-neutral-500">
            No classifiable enrollments for {formatPeriodLong(data?.period ?? null)}. RDE needs an
            effective date on the statement (disenrollment is inferred from the member timeline when
            the carrier doesn’t report a term date) — this carrier’s file may be missing effective
            dates, or the book has no drops yet.
          </p>
        </Card>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-3">
            <StatCard
              tone="rose"
              label={`Clawed back · ${formatPeriodLong(data?.period ?? null)}`}
              value={formatCurrency(radar.realized.clawedBackAmount)}
              sub={`${radar.realized.count} rapid disenrollment${radar.realized.count === 1 ? '' : 's'} reversed this period`}
            />
            <StatCard
              tone="amber"
              label="At risk in the RDE window"
              value={formatCurrency(radar.exposure.atRiskAmount)}
              sub={`${radar.exposure.count} active member${radar.exposure.count === 1 ? '' : 's'} still inside the first ${radar.rdeMonths} months`}
            />
            <StatCard
              tone="neutral"
              label="Rapid-disenroll rate"
              value={`${radar.disenrollments.rapidRatePct}%`}
              sub={`${radar.disenrollments.rapid} of ${radar.disenrollments.total} disenrollment${radar.disenrollments.total === 1 ? '' : 's'} were rapid`}
            />
          </div>

          {radar.inferredDisenrollments > 0 && (
            <p className="text-xs text-neutral-500">
              {radar.inferredDisenrollments} of {radar.disenrollments.total} disenrollment
              {radar.disenrollments.total === 1 ? ' was' : 's were'} <strong>inferred</strong> from
              members dropping off the statement — your carrier doesn’t report term dates, so we
              read disenrollment from the member timeline (present → absent).
            </p>
          )}
          {coverage != null && coverage < 100 && (
            <p className="text-xs text-neutral-500">
              Based on {radar.evaluated} of {radar.total} records we could classify ({coverage}%
              coverage) — the rest are missing an effective date and are never guessed.
            </p>
          )}

          <div className="grid gap-4 lg:grid-cols-2">
            <BreakdownCard title="By plan" rows={radar.byPlan} />
            <BreakdownCard title="By writing agent" rows={radar.byAgent} />
          </div>
        </>
      )}

      {carrierId && forecast?.forecast && forecast.forecast.evaluated > 0 && (
        <RenewalForecastSection data={forecast} />
      )}
    </div>
  );
}

function RenewalForecastSection({ data }: { data: ForecastResponse }) {
  const f = data.forecast;
  if (!f) return null;
  const max = Math.max(1, ...f.byMonth.map((m) => m.amount));
  const coverage = f.activeMembers > 0 ? Math.round((f.evaluated / f.activeMembers) * 100) : null;
  return (
    <div className="space-y-4 border-t border-neutral-200 pt-6 dark:border-neutral-800">
      <div>
        <h2 className="text-lg font-semibold">Renewal forecast</h2>
        <p className="mt-1 max-w-2xl text-sm text-neutral-500">
          What the active book renews over the next {f.horizonMonths} months — each member renews on
          their enrollment anniversary at the CMS renewal rate. Assumes retention (the RDE radar
          above is the downside).{' '}
          {data.ratesInferred && 'Rates are the bundled CMS maximums — an estimate.'}
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <StatCard
          tone="emerald"
          label="Annual recurring renewal income"
          value={formatCurrency(f.annualRenewalValue)}
          sub="Books often trade at a multiple of this"
        />
        <StatCard
          tone="neutral"
          label="Renewing members"
          value={String(f.evaluated)}
          sub={`of ${f.activeMembers} active${coverage != null && coverage < 100 ? ` · ${coverage}% priced` : ''}`}
        />
        <StatCard
          tone="neutral"
          label="Avg renewal / member"
          value={formatCurrency(f.evaluated > 0 ? f.annualRenewalValue / f.evaluated : 0)}
          sub="Per renewing member per year"
        />
      </div>

      <Card>
        <div className="mb-3 text-xs font-medium uppercase tracking-wide text-neutral-500">
          Projected renewals by month (anniversary seasonality)
        </div>
        <div className="flex items-end gap-1.5" style={{ height: 120 }}>
          {f.byMonth.map((m) => (
            <div key={m.period} className="flex flex-1 flex-col items-center justify-end gap-1">
              <div
                className="w-full rounded-t bg-emerald-500/80 transition-all hover:bg-emerald-500"
                style={{ height: `${(m.amount / max) * 100}%` }}
                title={`${m.period}: ${formatCurrency(m.amount)} · ${m.members} member${m.members === 1 ? '' : 's'}`}
              />
              <span className="text-[10px] text-neutral-400">{m.period.slice(5)}</span>
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-0">
        <div className="grid grid-cols-[2fr_0.8fr_1fr] gap-3 border-b border-neutral-200 px-5 py-2 text-xs font-medium text-neutral-500 dark:border-neutral-800">
          <span>Renewals by plan</span>
          <span className="text-right">Members</span>
          <span className="text-right">Annual renewal</span>
        </div>
        {f.byPlan.length === 0 ? (
          <p className="py-8 text-center text-sm text-neutral-500">No renewing members.</p>
        ) : (
          <div className="divide-y divide-neutral-100 dark:divide-neutral-800">
            {f.byPlan.slice(0, 12).map((p) => (
              <div
                key={p.key}
                className="grid grid-cols-[2fr_0.8fr_1fr] items-center gap-3 px-5 py-2.5 text-sm"
              >
                <span className="truncate" title={p.key}>
                  {p.key}
                </span>
                <span className="text-right text-neutral-600 dark:text-neutral-300">
                  {p.members}
                </span>
                <span className="text-right font-medium text-emerald-700 dark:text-emerald-400">
                  {formatCurrency(p.amount)}
                </span>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

function StatCard({
  tone,
  label,
  value,
  sub,
}: {
  tone: 'rose' | 'amber' | 'emerald' | 'neutral';
  label: string;
  value: string;
  sub: string;
}) {
  const valueClass =
    tone === 'rose'
      ? 'text-rose-600 dark:text-rose-400'
      : tone === 'amber'
        ? 'text-amber-600 dark:text-amber-400'
        : tone === 'emerald'
          ? 'text-emerald-600 dark:text-emerald-400'
          : 'text-neutral-900 dark:text-neutral-100';
  return (
    <Card>
      <div className="text-xs font-medium uppercase tracking-wide text-neutral-500">{label}</div>
      <div className={`mt-1 text-3xl font-semibold ${valueClass}`}>{value}</div>
      <div className="mt-1 text-xs text-neutral-500">{sub}</div>
    </Card>
  );
}

function BreakdownCard({ title, rows }: { title: string; rows: RdeBreakdownRow[] }) {
  return (
    <Card className="p-0">
      <div className="grid grid-cols-[1.6fr_0.7fr_0.7fr_1fr] gap-3 border-b border-neutral-200 px-5 py-2 text-xs font-medium text-neutral-500 dark:border-neutral-800">
        <span>{title}</span>
        <span className="text-right">Left</span>
        <span className="text-right">Rapid</span>
        <span className="text-right">Clawed back</span>
      </div>
      {rows.length === 0 ? (
        <p className="py-8 text-center text-sm text-neutral-500">No disenrollments this period.</p>
      ) : (
        <div className="divide-y divide-neutral-100 dark:divide-neutral-800">
          {rows.slice(0, 12).map((r) => (
            <div
              key={r.key}
              className="grid grid-cols-[1.6fr_0.7fr_0.7fr_1fr] items-center gap-3 px-5 py-2.5 text-sm"
            >
              <span className="truncate" title={r.key}>
                {r.key}
              </span>
              <span className="text-right text-neutral-600 dark:text-neutral-300">
                {r.disenrolled}
              </span>
              <span
                className={`text-right font-medium ${r.rapid > 0 ? 'text-rose-600 dark:text-rose-400' : 'text-neutral-400'}`}
              >
                {r.rapid}
              </span>
              <span className="text-right font-medium text-amber-600 dark:text-amber-400">
                {r.clawedBackAmount > 0 ? `−${formatCurrency(r.clawedBackAmount)}` : '—'}
              </span>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
